/**
 * AI Extraction Service
 *
 * API client for the AI extraction review feature.
 * Handles extraction triggering, suggestion retrieval, and approval/rejection.
 */

import axios from 'axios'
import { environment } from '../environments/environment'
import {
  AISuggestionsResponse,
  ExtractDiagnosesResponse,
  ApproveSuggestionsRequest,
  ApproveSuggestionsResponse,
  UploadLabPdfsResponse,
  ExtractLabsResponse,
  ExtractionType,
} from '../model/ai-extraction.types'

const apiEndpoint = environment.webservices.endpoint
const JSON_HEADERS = { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } }

export class AIExtractionService {
  /**
   * Trigger AI extraction of diagnoses and/or measurements from CCDs
   * @param patientId - Patient ref_id
   * @param focus - 'dx' (diagnoses only), 'meas' (measurements only), 'allergy' (allergies from CCD), or 'all' (both)
   */
  extractDiagnoses(
    patientId: string,
    focus: ExtractionType = 'all'
  ): Promise<{ data: ExtractDiagnosesResponse }> {
    const endpoint = `${apiEndpoint}/patients/${patientId}/extract_diagnoses?focus=${focus}`
    // 45 minute timeout - extraction involves LLM processing of multiple document chunks
    return axios.get(endpoint, { timeout: 2700000 })
  }

  /**
   * Get pending AI-extracted suggestions for review
   * @param patientId - Patient ref_id
   * @param type - Optional filter by 'diagnosis' or 'measurement'
   * @param status - Optional filter by status (default: 'pending')
   * @param extractionLogId - Optional filter by specific extraction batch
   */
  getSuggestions(
    patientId: string,
    type?: 'diagnosis' | 'measurement',
    status?: string,
    extractionLogId?: number
  ): Promise<{ data: AISuggestionsResponse }> {
    let endpoint = `${apiEndpoint}/patients/${patientId}/ai-suggestions`
    const params: string[] = []

    if (type) params.push(`type=${type}`)
    if (status) params.push(`status=${status}`)
    if (extractionLogId) params.push(`extraction_log_id=${extractionLogId}`)

    if (params.length > 0) {
      endpoint += '?' + params.join('&')
    }

    return axios.get(endpoint)
  }

  /**
   * Approve or reject AI-extracted suggestions
   * @param patientId - Patient ref_id
   * @param request - Approval/rejection request with IDs and reasons
   */
  approveSuggestions(
    patientId: string,
    request: ApproveSuggestionsRequest
  ): Promise<{ data: ApproveSuggestionsResponse }> {
    const endpoint = `${apiEndpoint}/patients/${patientId}/approve_diagnoses`
    return axios.put(endpoint, request, JSON_HEADERS)
  }

  /**
   * Undo a single reviewed suggestion, returning it to 'pending'.
   * Supported for rejected suggestions and approved measurements; reverting an
   * approved diagnosis returns 400 (remove it from the diagnoses panel instead).
   * @param patientId - Patient ref_id
   * @param suggestionId - Suggestion id to revert
   */
  revertSuggestion(
    patientId: string,
    suggestionId: number
  ): Promise<{ data: { id: number; status: string } }> {
    const endpoint = `${apiEndpoint}/patients/${patientId}/ai-suggestions/${suggestionId}/revert`
    return axios.put(endpoint, {}, JSON_HEADERS)
  }

  /**
   * Upload lab PDF files for a patient
   * @param patientId - Patient ref_id
   * @param files - Array of File objects to upload
   * @param processImmediately - Whether to trigger extraction after upload
   */
  uploadLabPdfs(
    patientId: string,
    files: File[],
    processImmediately: boolean = false
  ): Promise<{ data: UploadLabPdfsResponse }> {
    const endpoint = `${apiEndpoint}/patients/${patientId}/upload-lab-pdfs`
    const formData = new FormData()

    files.forEach((file) => {
      formData.append('files', file)
    })

    if (processImmediately) {
      formData.append('process_immediately', 'true')
    }

    return axios.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  /**
   * Process uploaded lab PDFs and stage measurements for review
   * @param patientId - Patient ref_id
   */
  extractLabs(patientId: string): Promise<{ data: ExtractLabsResponse }> {
    const endpoint = `${apiEndpoint}/patients/${patientId}/extract-labs`
    // 10 minute timeout - extraction involves LLM processing
    return axios.post(endpoint, {}, { timeout: 600000 })
  }

  /**
   * Get AI extraction history for a patient
   * @param patientId - Patient ref_id
   */
  getExtractionLogs(patientId: string): Promise<{ data: any }> {
    const endpoint = `${apiEndpoint}/patients/${patientId}/ai-extraction-logs`
    return axios.get(endpoint)
  }
}

// Export singleton instance
export const aiExtractionService = new AIExtractionService()
